import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { TicketComposeFields, type ComposeMode } from '@/components/ticket-detail/TicketComposeFields.js';
import { getEntityAuditLog } from '@/lib/api/audit-log.js';
import { useAuthStore } from '@/stores/auth.js';
import type {
  TicketCommunication,
  TicketEvent,
  TicketNote,
} from '@/lib/api/tickets.js';
import { formatDateTime, prettyLabel } from '@/lib/format.js';
import { formatTicketStatus } from '@/components/tickets/StatusBadge.js';

type TimelineItem =
  | { kind: 'note'; id: string; at: string; data: TicketNote }
  | { kind: 'comm'; id: string; at: string; data: TicketCommunication }
  | { kind: 'event'; id: string; at: string; data: TicketEvent };

function eventLabel(event: TicketEvent) {
  const overrideSuffix = (() => {
    const name =
      event.payload.approvedByName != null
        ? String(event.payload.approvedByName)
        : event.payload.authorizedByName != null
          ? String(event.payload.authorizedByName)
          : null;
    if (!name && !event.payload.managerOverrideAuthorizedBy) return '';
    return name ? ` (Manager override by ${name})` : ' (Manager override)';
  })();

  if (event.eventType === 'ticket.status_changed') {
    const derived = event.payload.derived ? ' (derived)' : '';
    const override = event.payload.override ? ' (manual override)' : '';
    const base = `Status changed: ${formatTicketStatus(String(event.payload.from ?? ''))} → ${formatTicketStatus(String(event.payload.to ?? ''))}`;
    return `${base}${derived}${override}${overrideSuffix}`;
  }
  if (event.eventType === 'ticket.device_updated' && event.payload.field === 'workStatus') {
    const derived = event.payload.derived ? ' (derived)' : '';
    return `Device status: ${prettyLabel(String(event.payload.from ?? ''))} → ${prettyLabel(String(event.payload.to ?? ''))}${derived}`;
  }
  if (event.eventType === 'ticket.discount_applied') {
    const pct = event.payload.discountPct != null ? `${event.payload.discountPct}%` : 'Discount';
    return `${pct} discount applied${overrideSuffix}`;
  }
  if (event.eventType === 'ticket.deposit_skipped') {
    return `Deposit waived${overrideSuffix}`;
  }
  if (event.eventType === 'ticket.repair_updated') {
    const serviceName = event.payload.serviceName ? String(event.payload.serviceName) : null;
    const action = event.payload.action ? String(event.payload.action) : null;
    if (action === 'removed' && serviceName) return `Repair removed: ${serviceName}`;
    if (action === 'cancelled' && serviceName) return `Repair cancelled: ${serviceName}`;
    if (action === 'price_updated' && serviceName) {
      const prev = event.payload.previousQuotedPrice != null ? String(event.payload.previousQuotedPrice) : null;
      const next = event.payload.quotedPrice != null ? String(event.payload.quotedPrice) : null;
      if (prev && next) return `Repair price updated (${serviceName}): $${prev} → $${next}${overrideSuffix}`;
      return `Repair price updated: ${serviceName}${overrideSuffix}`;
    }
  }
  if (event.eventType === 'ticket.assigned') {
    const reason = event.payload.reason ? String(event.payload.reason) : null;
    const auto = event.payload.auto === true;
    if (auto && reason) {
      const human = reason
        .replace(/^auto_assign_/, '')
        .replace(/^preferred_from_source$/, 'from appointment/source')
        .replace(/_/g, ' ');
      return `Auto-assigned (${human})`;
    }
    return 'Assigned technician';
  }
  if (event.eventType === 'ticket.complaint_updated') {
    const to = event.payload.to != null ? String(event.payload.to).trim() : '';
    return to ? `Customer complaint updated: “${to.slice(0, 80)}${to.length > 80 ? '…' : ''}”` : 'Customer complaint cleared';
  }
  if (event.eventType === 'ticket.intake_notes_updated') {
    const to = event.payload.to != null ? String(event.payload.to).trim() : '';
    return to
      ? `Internal diagnostic notes updated: “${to.slice(0, 80)}${to.length > 80 ? '…' : ''}”`
      : 'Internal diagnostic notes cleared';
  }
  return event.eventType.replace(/^ticket\./, '').replace(/_/g, ' ');
}

export function CommunicationTimeline({
  ticketId,
  notes,
  communications,
  events,
  composeMode,
  onComposeModeChange,
  composeBody,
  onComposeBodyChange,
  smsPhone,
  onSmsPhoneChange,
  emailTo,
  onEmailToChange,
  emailSubject,
  onEmailSubjectChange,
  onSubmit,
  submitting,
  hideComposer = false,
}: {
  ticketId?: string;
  notes: TicketNote[];
  communications: TicketCommunication[];
  events: TicketEvent[];
  composeMode: ComposeMode;
  onComposeModeChange: (mode: ComposeMode) => void;
  composeBody: string;
  onComposeBodyChange: (v: string) => void;
  smsPhone: string;
  onSmsPhoneChange: (v: string) => void;
  emailTo: string;
  onEmailToChange: (v: string) => void;
  emailSubject: string;
  onEmailSubjectChange: (v: string) => void;
  onSubmit: () => void;
  submitting: boolean;
  hideComposer?: boolean;
}) {
  const [tab, setTab] = useState<'all' | 'private' | 'comms' | 'attachments' | 'system'>('all');
  const user = useAuthStore((s) => s.user);
  const canAudit = ['tenant_admin', 'platform_admin'].includes(user?.role ?? '');

  const auditQuery = useQuery({
    queryKey: ['audit-log-entity', 'ticket', ticketId],
    queryFn: () => getEntityAuditLog('ticket', ticketId!),
    enabled: Boolean(ticketId && canAudit && tab === 'system'),
  });

  const items = useMemo(() => {
    const merged: TimelineItem[] = [
      ...notes.map((n) => ({ kind: 'note' as const, id: n.id, at: n.createdAt, data: n })),
      ...(communications ?? []).map((c) => ({ kind: 'comm' as const, id: c.id, at: c.createdAt, data: c })),
      ...events.map((e) => ({ kind: 'event' as const, id: e.id, at: e.createdAt, data: e })),
    ].sort((a, b) => +new Date(b.at) - +new Date(a.at));

    return merged.filter((item) => {
      if (tab === 'all') return true;
      if (tab === 'private') return item.kind === 'note' && item.data.visibility === 'private';
      if (tab === 'comms') return item.kind === 'comm';
      if (tab === 'attachments') {
        return item.kind === 'note' && Array.isArray(item.data.attachments) && item.data.attachments.length > 0;
      }
      if (tab === 'system') return item.kind === 'event';
      return true;
    });
  }, [notes, communications, events, tab]);

  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 p-4">
        <h2 className="text-lg font-bold text-slate-950">Communication</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {([
            ['all', 'All'],
            ['private', 'Private Notes'],
            ['comms', 'Email/SMS'],
            ['attachments', 'Attachments'],
            ['system', 'System Messages'],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${tab === key ? 'bg-primary text-white' : 'bg-slate-100 text-slate-700'}`}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-h-[480px] space-y-3 overflow-y-auto p-4">
        {items.map((item) => {
          if (item.kind === 'note') {
            const note = item.data;
            const isPrivate = note.visibility === 'private';
            return (
              <div key={item.id} className={`rounded-xl border p-4 ${isPrivate ? 'border-slate-300 bg-slate-50' : 'border-green-100 bg-green-50/40'}`}>
                <div className="mb-2 flex justify-between text-xs text-slate-500">
                  <span className="font-bold uppercase">{isPrivate ? '🔒 Private Note' : '💬 Note'}</span>
                  <span>{formatDateTime(note.createdAt)}</span>
                </div>
                <p className="whitespace-pre-wrap text-sm text-slate-800">{note.body}</p>
              </div>
            );
          }
          if (item.kind === 'comm') {
            const comm = item.data;
            const isSms = comm.channel === 'sms';
            const inbound = comm.direction === 'inbound';
            return (
              <div
                key={item.id}
                className={`rounded-xl border p-4 ${
                  inbound ? 'border-slate-200 bg-slate-50' : 'border-blue-100 bg-blue-50/50'
                }`}
              >
                <div className="mb-2 flex justify-between text-xs text-slate-500">
                  <span className="flex flex-wrap items-center gap-2 font-bold uppercase">
                    {isSms
                      ? inbound
                        ? '📱 SMS Received'
                        : '📱 SMS Sent'
                      : inbound
                        ? '📧 Email Received'
                        : '📧 Email Sent'}
                    {comm.dryRun ? (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold normal-case tracking-normal text-amber-900">
                        DRY RUN
                      </span>
                    ) : null}
                  </span>
                  <span>{formatDateTime(comm.createdAt)}</span>
                </div>
                <p className="text-xs text-slate-600">
                  {inbound ? `From: ${comm.fromAddress ?? comm.toAddress}` : `To: ${comm.toAddress}`}
                </p>
                {comm.subject ? <p className="text-xs font-semibold text-slate-700">Subject: {comm.subject}</p> : null}
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-800">{comm.body}</p>
                <p className="mt-1 text-xs text-slate-500">Status: {comm.status}</p>
                {isSms && inbound ? (
                  <Link
                    to="/messages"
                    className="mt-2 inline-block text-xs font-semibold text-primary hover:underline"
                  >
                    Reply in Messages →
                  </Link>
                ) : null}
              </div>
            );
          }
          return (
            <div key={item.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="mb-1 flex justify-between text-xs text-slate-500">
                <span className="font-bold uppercase">⚙ System</span>
                <span>{formatDateTime(item.data.createdAt)}</span>
              </div>
              <p className="text-sm text-slate-700">{eventLabel(item.data)}</p>
            </div>
          );
        })}
        {items.length === 0 ? <p className="text-center text-sm text-slate-500">No messages yet.</p> : null}
        {tab === 'system' && canAudit && ticketId && auditQuery.data?.length ? (
          <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-3">
            <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Audit trail</p>
            <ul className="space-y-2">
              {auditQuery.data.map((entry) => {
                const approvedBy =
                  typeof entry.metadata?.approvedByName === 'string'
                    ? entry.metadata.approvedByName
                    : typeof entry.metadata?.authorizedByName === 'string'
                      ? entry.metadata.authorizedByName
                      : null;
                return (
                  <li key={entry.id} className="rounded-lg bg-white p-2 text-xs ring-1 ring-slate-200">
                    <span className="font-semibold text-slate-800">{entry.action}</span>
                    <span className="text-slate-500">
                      {' '}
                      · {entry.userName ?? 'System'} · {formatDateTime(entry.createdAt)}
                    </span>
                    {approvedBy ? (
                      <p className="mt-0.5 font-semibold text-emerald-700">
                        ✓ Override approved by {approvedBy}
                      </p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </div>

      {hideComposer ? null : (
        <div className="border-t border-slate-200 p-4">
          <TicketComposeFields
            composeMode={composeMode}
            onComposeModeChange={onComposeModeChange}
            composeBody={composeBody}
            onComposeBodyChange={onComposeBodyChange}
            smsPhone={smsPhone}
            onSmsPhoneChange={onSmsPhoneChange}
            emailTo={emailTo}
            onEmailToChange={onEmailToChange}
            emailSubject={emailSubject}
            onEmailSubjectChange={onEmailSubjectChange}
            onSubmit={onSubmit}
            submitting={submitting}
          />
        </div>
      )}
    </section>
  );
}
